import {
  Component, OnInit, OnDestroy, Input, Output, EventEmitter,
  AfterViewInit, ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import XHRUpload from '@uppy/xhr-upload';
import Turkish from '@uppy/locales/lib/tr_TR';
import English from '@uppy/locales/lib/en_US';

import * as Communication from '../../communication/index';
import { Config } from '../../../environments/environment';
import { Authorization } from '../authorization/authorization.component';
import { NotificationService } from '../common/NotificationService';

declare var $: any;

@Component({
  selector: 'app-attachments-modal',
  templateUrl: 'attachments-modal.component.html',
  styleUrls: [
    'attachments-modal.component.css',
    '../../../../../node_modules/@uppy/core/dist/style.css',
    '../../../../../node_modules/@uppy/dashboard/dist/style.css',
  ],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, DragDropModule],
  encapsulation: ViewEncapsulation.None
})
export class AttachmentsModalComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() documentId: number;
  @Input() requestCode: string;
  @Input() editable: boolean = false;
  @Input() jobName: string = '';

  @Output() modalClosed = new EventEmitter<void>();
  @Output() attachmentsChanged = new EventEmitter<number>();

  attachments: any[] = [];
  count: number = 0;
  maxAllowed: number = 50;
  loading: boolean = false;

  private uppy: Uppy;
  private subscriptions: Subscription[] = [];

  constructor(
    private attachmentsApi: Communication.AttachmentsService,
    private authorization: Authorization,
    private notificationService: NotificationService,
    public translate: TranslateService
  ) {}

  get isRecipientMode(): boolean {
    return !!this.requestCode;
  }

  ngOnInit() {
    this.loadAttachments();

    // Listen for modal hidden event to emit close
    setTimeout(() => {
      $('#attachmentsModal').on('hidden.bs.modal', () => {
        this.modalClosed.emit();
      });
    }, 200);
  }

  ngAfterViewInit() {
    if (this.editable) {
      // Delay Uppy init to ensure the DOM target exists
      setTimeout(() => this.initUppy(), 300);
    }
  }

  ngOnDestroy() {
    if (this.uppy) {
      this.uppy.destroy();
    }
    this.subscriptions.forEach(s => s.unsubscribe());
    $('#attachmentsModal').off('hidden.bs.modal');
  }

  loadAttachments(): void {
    this.loading = true;
    if (this.isRecipientMode) {
      this.subscriptions.push(
        this.attachmentsApi.recipientRequestsCodeAttachmentsGet(this.requestCode).subscribe(
          (response: Communication.AttachmentListResponse) => {
            this.attachments = (response.attachments || []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
            this.count = response.count || 0;
            this.maxAllowed = response.maxAllowed || 15;
            this.loading = false;
            this.attachmentsChanged.emit(this.count);
          },
          (err) => {
            console.error('Error loading attachments:', err);
            this.loading = false;
          }
        )
      );
    } else {
      this.subscriptions.push(
        this.attachmentsApi.documentsIdAttachmentsGet(
          this.authorization.getAccessToken(), this.documentId
        ).subscribe(
          (response: Communication.AttachmentListResponse) => {
            this.attachments = (response.attachments || []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
            this.count = response.count || 0;
            this.maxAllowed = response.maxAllowed || 15;
            this.loading = false;
            this.attachmentsChanged.emit(this.count);
            this.updateUppyRestrictions();
          },
          (err) => {
            console.error('Error loading attachments:', err);
            this.loading = false;
          }
        )
      );
    }
  }

  downloadAttachment(attachment: any): void {
    const name = attachment.displayName || attachment.name;
    if (this.isRecipientMode) {
      this.subscriptions.push(
        this.attachmentsApi.recipientRequestsCodeAttachmentsAttachmentIdGet(
          this.requestCode, attachment.id
        ).subscribe(
          (data: Blob) => this.triggerDownload(data, name),
          (err) => {
            console.error('Error downloading attachment:', err);
            this.notificationService.attachmentNotFound();
          }
        )
      );
    } else {
      this.subscriptions.push(
        this.attachmentsApi.attachmentsAttachmentIdGet(
          this.authorization.getAccessToken(), attachment.id
        ).subscribe(
          (data: Blob) => this.triggerDownload(data, name),
          (err) => {
            console.error('Error downloading attachment:', err);
            this.notificationService.attachmentNotFound();
          }
        )
      );
    }
  }

  downloadAllAsZip(): void {
    const zipName = (this.jobName || 'attachments') + '_attachments.zip';
    if (this.isRecipientMode) {
      this.subscriptions.push(
        this.attachmentsApi.recipientRequestsCodeAttachmentsDownloadGet(this.requestCode).subscribe(
          (data: Blob) => this.triggerDownload(data, zipName),
          (err) => {
            console.error('Error downloading ZIP:', err);
            this.notificationService.attachmentOperationFailed();
          }
        )
      );
    } else {
      this.subscriptions.push(
        this.attachmentsApi.documentsIdAttachmentsDownloadGet(
          this.authorization.getAccessToken(), this.documentId
        ).subscribe(
          (data: Blob) => this.triggerDownload(data, zipName),
          (err) => {
            console.error('Error downloading ZIP:', err);
            this.notificationService.attachmentOperationFailed();
          }
        )
      );
    }
  }

  // --- Owner-only methods ---

  startRename(attachment: any): void {
    if (!this.editable) return;
    attachment._editing = true;
    attachment._editName = attachment.displayName || attachment.name;
  }

  cancelRename(attachment: any): void {
    attachment._editing = false;
    attachment._editName = '';
  }

  saveRename(attachment: any): void {
    const newName = (attachment._editName || '').trim();
    if (!newName) {
      this.cancelRename(attachment);
      return;
    }
    const body: Communication.AttachmentUpdateRequest = { displayName: newName };
    this.subscriptions.push(
      this.attachmentsApi.attachmentsAttachmentIdPut(
        this.authorization.getAccessToken(), attachment.id, body
      ).subscribe(
        () => {
          attachment.displayName = newName;
          attachment._editing = false;
          attachment._editName = '';
        },
        (err) => {
          console.error('Error renaming attachment:', err);
          if (err.status === 409) {
            this.notificationService.attachmentAlreadyExists();
          } else {
            this.notificationService.attachmentOperationFailed();
          }
        }
      )
    );
  }

  confirmDelete(attachment: any): void {
    if (!this.editable) return;
    const name = attachment.displayName || attachment.name;
    this.notificationService.showAttachmentDeleteAlert(name, (isConfirmed: boolean) => {
      if (isConfirmed) {
        this.deleteAttachment(attachment);
      }
    });
  }

  private deleteAttachment(attachment: any): void {
    this.subscriptions.push(
      this.attachmentsApi.attachmentsAttachmentIdDelete(
        this.authorization.getAccessToken(), attachment.id
      ).subscribe(
        () => {
          this.loadAttachments();
        },
        (err) => {
          console.error('Error deleting attachment:', err);
          this.notificationService.attachmentOperationFailed();
        }
      )
    );
  }

  // --- Reorder ---

  onDrop(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.attachments, event.previousIndex, event.currentIndex);
    this.reorderAttachments();
  }

  sortAlphabetically(): void {
    this.attachments.sort((a, b) => {
      const nameA = (a.displayName || a.name || '').toLowerCase();
      const nameB = (b.displayName || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    this.reorderAttachments();
  }

  private reorderAttachments(): void {
    const attachmentIds = this.attachments.map(a => a.id).join(',');
    this.subscriptions.push(
      this.attachmentsApi.documentsIdAttachmentsReorderPut(
        this.authorization.getAccessToken(),
        this.documentId,
        attachmentIds
      ).subscribe(
        (response: Communication.AttachmentListResponse) => {
          // If the server returns the full list, use it; otherwise keep optimistic order
          if (response?.attachments?.length) {
            this.attachments = response.attachments;
            this.count = response.count || this.attachments.length;
          }
        },
        (err) => {
          console.error('Error reordering attachments:', err);
          this.notificationService.attachmentOperationFailed();
          this.loadAttachments();
        }
      )
    );
  }

  // --- Helpers ---

  getFileIcon(mimeType: string): string {
    if (!mimeType) return 'icon-file-empty';
    if (mimeType.startsWith('image/')) return 'icon-file-picture';
    if (mimeType === 'application/pdf') return 'icon-file-pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'icon-file-word';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'icon-file-excel';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'icon-file-presentation';
    if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'icon-file-zip';
    return 'icon-file-empty';
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  private triggerDownload(data: Blob, filename: string): void {
    const url = window.URL.createObjectURL(data);
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', url);
    linkElement.setAttribute('download', filename);
    const clickEvent = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: false
    });
    linkElement.dispatchEvent(clickEvent);
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  }

  private initUppy(): void {
    this.uppy = new Uppy({
      autoProceed: true,
      locale: this.translate.currentLang === 'tr' ? Turkish : English,
      restrictions: {
        maxFileSize: 100 * 1024 * 1024,
        maxNumberOfFiles: Math.max(this.maxAllowed - this.count, 1),
      },
      onBeforeFileAdded: (currentFile) => {
        if (this.count >= this.maxAllowed) {
          this.notificationService.attachmentLimitExceeded();
          return false;
        }
        return currentFile;
      }
    });

    this.uppy.use(Dashboard, {
      inline: true,
      target: '#attachment-uppy-dashboard',
      height: 200,
      hideProgressDetails: false,
      proudlyDisplayPoweredByUppy: false,
    });

    this.uppy.use(XHRUpload, {
      endpoint: Config.WEBSERVICEURL + '/documents/' + this.documentId + '/attachments',
      method: 'POST',
      formData: true,
      fieldName: 'file',
      bundle: false,
      allowedMetaFields: ['displayName'],
    });

    // Store original filename as displayName so UTF-8 characters are preserved
    this.uppy.on('file-added', (file) => {
      this.uppy.setFileMeta(file.id, {
        displayName: file.name || ''
      });
    });

    this.uppy.on('upload', async () => {
      const token = this.authorization.getAccessToken();
      const xhr = this.uppy.getPlugin<XHRUpload<any, any>>('XHRUpload');
      xhr?.setOptions({
        headers: { 'access_token': token }
      });
    });

    this.uppy.on('upload-success', (file) => {
      this.loadAttachments();
      if (file) {
        this.uppy.removeFile(file.id);
      }
    });

    this.uppy.on('upload-error', (file, error, response) => {
      const status = (response as any)?.status;
      if (status === 409) {
        this.notificationService.attachmentAlreadyExists();
      } else if (status === 400) {
        this.notificationService.attachmentOperationFailed();
      } else {
        this.notificationService.attachmentOperationFailed();
      }
    });
  }

  private updateUppyRestrictions(): void {
    if (this.uppy) {
      this.uppy.setOptions({
        restrictions: {
          maxFileSize: 100 * 1024 * 1024,
          maxNumberOfFiles: Math.max(this.maxAllowed - this.count, 1),
        }
      });
    }
  }
}
